import { useCallback, useMemo } from 'react';
import { useInstantSearch, useRefinementList } from 'react-instantsearch';

import { FilterTitle } from '@sorare/core/src/components/search/FilterTitle';
import { AtomList } from '@sorare/core/src/components/search/RefineList';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { FILTERS } from '@sorare/core/src/lib/filters';

import type { IndexedPosition } from '#src/lib/mlbMarketPositions.ts';
import {
  MAPPING,
  REVERSE_MAPPING,
  lineupPositionNames,
  positions,
} from '#src/lib/mlbMarketPositions.ts';

const FILTER = FILTERS.position;

const MLBPositionRefineList = ({ visible }: { visible: boolean }) => {
  const track = useEvents();
  const { items, refine: doRefine } = useRefinementList({
    attribute: FILTER.attribute,
    limit: 10,
  });
  const { formatMessage } = useIntlContext();
  const { indexUiState } = useInstantSearch();

  const refine = useCallback(
    (value: string) => {
      track('Use Market Filter', {
        filterName: FILTER.attribute,
        filterValue: value,
      });

      Object.entries(MAPPING)
        .map(([v, position]) => (position === value ? v : undefined))
        .filter(Boolean)
        .forEach(v => doRefine(v));
    },
    [doRefine, track]
  );

  const actualItems = useMemo(() => {
    const uiRefinements = indexUiState.refinementList?.[FILTER.attribute] || [];

    return positions.map(position => ({
      value: position,
      label: formatMessage(lineupPositionNames[position]),
      isRefined: uiRefinements.some(refinement =>
        REVERSE_MAPPING[position].includes(refinement as IndexedPosition)
      ),
      count: items.reduce((total, item) => {
        return (
          total +
          (MAPPING[item.value as IndexedPosition] === position ? item.count : 0)
        );
      }, 0),
    }));
  }, [formatMessage, indexUiState.refinementList, items]);

  return (
    <AtomList
      attribute={FILTER.attribute}
      items={actualItems}
      refine={refine}
      visible={visible}
    />
  );
};

const MLBPositionRefineListTitle = () => {
  const { indexUiState } = useInstantSearch();

  const refinements = indexUiState.refinementList?.[FILTER.attribute] || [];
  const selected = new Set(refinements.map(v => MAPPING[v as IndexedPosition]))
    .size;

  return <FilterTitle name={FILTER.title} selected={selected} />;
};

export const RefineMLBPosition: FilterWidget = {
  key: FILTER.attribute,
  type: 'list',
  attribute: FILTER.attribute,
  Component: ({ visible }) => <MLBPositionRefineList visible={visible} />,
  title: <MLBPositionRefineListTitle />,
  accordionOptions: { startsOpen: true },
};
